
import React, { useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { COUNTRIES, PACKAGES } from '../data';
import { PackageCard } from './Home';

const FactWidget: React.FC<{ icon: string; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 md:gap-5 p-5 md:p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-left">
    <div className="w-10 h-10 md:w-12 md:h-12 bg-brand-surface rounded-lg flex items-center justify-center text-brand-accent text-lg md:text-xl flex-shrink-0">
      <i className={icon}></i>
    </div>
    <div>
      <p className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-brand-deep/30 mb-0.5">{label}</p>
      <p className="text-xs md:text-sm font-black text-brand-deep">{value}</p>
    </div>
  </div>
);

export const CountryDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const country = useMemo(() => COUNTRIES.find(c => c.slug === slug), [slug]);
  const countryPackages = useMemo(() => 
    PACKAGES.filter(p => p.country === country?.name).sort((a, b) => b.popularity - a.popularity), 
    [country]
  );

  if (!country) return (
    <div className="py-32 text-center px-6">
      <h1 className="text-3xl md:text-4xl font-black mb-8">Galamērķis nav atrasts</h1>
      <Link to="/galamerki" className="bg-brand-accent text-white px-8 py-4 rounded-lg font-black uppercase text-xs tracking-widest">Atpakaļ uz sarakstu</Link>
    </div>
  );

  return (
    <div className="bg-[#F8FAFC] pb-20 md:pb-32 overflow-x-hidden">
      {/* Immersive Top Bar / Breadcrumbs - Visible on tablet+ */}
      <div className="hidden sm:block bg-white border-b border-gray-100 py-4">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex items-center justify-between">
          <div className="flex items-center gap-4 text-[9px] font-black uppercase tracking-widest text-brand-deep/30">
            <Link to="/" className="hover:text-brand-accent transition-colors">Sākums</Link>
            <i className="fas fa-chevron-right text-[8px] opacity-20"></i>
            <Link to="/galamerki" className="hover:text-brand-accent transition-colors">Galamērķi</Link>
            <i className="fas fa-chevron-right text-[8px] opacity-20"></i>
            <span className="text-brand-deep">{country.name}</span>
          </div>
          <button 
            onClick={() => navigate('/celojumi')}
            className="text-brand-accent font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:gap-3 transition-all"
          >
            Skatīt visus piedāvājumus <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>

      {/* Hero Header - Standardized Height */}
      <section className="relative h-[500px] md:h-[600px] overflow-hidden bg-brand-deep">
        <img 
          src={country.image} 
          className="absolute inset-0 w-full h-full object-cover brightness-[0.4]" 
          alt={country.name} 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-deep via-transparent to-transparent opacity-80"></div>
        
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 flex flex-col justify-end pb-12 md:pb-20">
          <div className="max-w-4xl space-y-6 md:space-y-8 animate-fade-in text-left">
            <div className="inline-block bg-brand-accent text-white px-5 py-2 rounded-full text-[10px] md:text-[11px] font-black uppercase tracking-[0.3em] shadow-lg">
              Atklājiet {country.name}
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter leading-none uppercase">
              {country.name}
            </h1>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-6 md:gap-10 pt-4">
               <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-white/10 backdrop-blur-md rounded-lg flex items-center justify-center text-brand-accent text-lg md:text-2xl border border-white/20">
                    <i className="fas fa-sun"></i>
                  </div>
                  <div>
                    <p className="text-[8px] md:text-[11px] font-black uppercase tracking-widest text-white opacity-60">Periods</p>
                    <p className="text-sm md:text-xl font-black tracking-tight text-white leading-tight">Maijs - Okt</p>
                  </div>
               </div>
               <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-white/10 backdrop-blur-md rounded-lg flex items-center justify-center text-brand-accent text-lg md:text-2xl border border-white/20">
                    <i className="fas fa-plane-arrival"></i>
                  </div>
                  <div>
                    <p className="text-[8px] md:text-[11px] font-black uppercase tracking-widest text-white opacity-60">Lidojums</p>
                    <p className="text-sm md:text-xl font-black tracking-tight text-white leading-tight">Tiešais</p>
                  </div>
               </div>
               <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-white/10 backdrop-blur-md rounded-lg flex items-center justify-center text-brand-accent text-lg md:text-2xl border border-white/20">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div>
                    <p className="text-[8px] md:text-[11px] font-black uppercase tracking-widest text-white opacity-60">Vīza</p>
                    <p className="text-sm md:text-xl font-black tracking-tight text-white leading-tight">Nav nepiec.</p>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-20 -mt-8 md:-mt-12 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          
          {/* Left Column */}
          <div className="lg:col-span-8 space-y-10 md:space-y-12">
            
            {/* About Section */}
            <div className="bg-white p-8 md:p-16 rounded-2xl shadow-xl border border-gray-100 text-left">
              <h2 className="text-2xl md:text-4xl font-black text-brand-deep tracking-tighter mb-8 md:mb-10">Kāpēc izvēlēties <span className="text-brand-accent">{country.name}?</span></h2>
              <div className="prose prose-lg md:prose-xl max-w-none text-brand-deep/80 leading-relaxed font-medium space-y-6 md:space-y-8">
                <p className="text-base md:text-xl font-black text-brand-deep leading-relaxed italic border-l-4 md:border-l-8 border-brand-accent pl-6 md:pl-10 mb-8 md:mb-10">
                  {country.description}
                </p>
                <p className="text-sm md:text-base">
                  {country.name} ir galamērķis, kas apbur ar savu daudzveidību. Neatkarīgi no tā, vai jūs meklējat mierpilnu atpūtu pie kristāldzidras jūras vai piedzīvojumus.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-10 md:mt-16">
                 {country.highlights.map((highlight, idx) => (
                   <div key={idx} className="flex items-center gap-4 md:gap-6 p-4 md:p-6 bg-brand-surface rounded-xl border border-transparent hover:border-brand-accent transition-all group">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-lg flex items-center justify-center text-brand-accent text-lg flex-shrink-0 group-hover:bg-brand-accent group-hover:text-white transition-all">
                        <i className="fas fa-check"></i>
                      </div>
                      <span className="font-black text-brand-deep tracking-tight text-xs md:text-sm uppercase leading-tight text-left">{highlight}</span>
                   </div>
                 ))}
              </div>
            </div>

            {/* Resorts Section */}
            <div className="text-left">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-10 gap-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-brand-accent mb-2">Kur palikt?</p>
                  <h2 className="text-2xl md:text-4xl font-black text-brand-deep tracking-tighter">Populārākie kūrorti</h2>
                </div>
                <button 
                  onClick={() => navigate(`/celojumi?destination=${encodeURIComponent(country.name)}`)}
                  className="text-brand-deep font-black text-[10px] md:text-xs uppercase tracking-widest border-b-2 border-brand-deep pb-1 hover:text-brand-accent hover:border-brand-accent transition-all"
                >
                  Skatīt visus galamērķus
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {country.cities.map((city) => (
                  <div 
                    key={city.id} 
                    onClick={() => navigate(`/celojumi?destination=${encodeURIComponent(city.name)}`)}
                    className="group relative h-64 md:h-80 rounded-lg overflow-hidden cursor-pointer shadow-xl hover:-translate-y-2 transition-all duration-500"
                  >
                    <img 
                      src={`https://picsum.photos/seed/${city.name}/800/600`} 
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                      alt={city.name} 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/90 via-transparent to-transparent group-hover:from-brand-accent/80 transition-all duration-500"></div>
                    <div className="absolute bottom-6 left-6 right-6 md:bottom-10 md:left-10 md:right-10 flex justify-between items-end">
                      <div>
                        <h4 className="text-2xl md:text-3xl font-black text-white tracking-tight mb-1">{city.name}</h4>
                        <div className="flex items-center gap-2 text-white/60 text-[9px] md:text-xs font-black uppercase tracking-widest">
                          <i className="fas fa-map-marker-alt text-brand-accent"></i> 
                          Piedāvājumi
                        </div>
                      </div>
                      <div className="w-10 h-10 md:w-14 md:h-14 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center text-white border border-white/20 group-hover:bg-white group-hover:text-brand-accent transition-all">
                        <i className="fas fa-arrow-right text-base"></i>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Quick Facts & Support */}
          <aside className="lg:col-span-4 space-y-8 md:space-y-10 w-full">
            <div className="bg-white p-8 md:p-10 rounded-lg shadow-xl border border-gray-100 text-left">
               <h3 className="text-lg md:text-xl font-black text-brand-deep tracking-tight mb-6 md:mb-8">Noderīga informācija</h3>
               <div className="grid grid-cols-1 gap-4">
                  <FactWidget icon="fas fa-coins" label="Valūta" value="Eiro (EUR)" />
                  <FactWidget icon="fas fa-language" label="Valoda" value="Valsts valoda" />
                  <FactWidget icon="fas fa-cloud-sun" label="Klimats" value="Subtropu" />
                  <FactWidget icon="fas fa-plug" label="Elektrība" value="230V" />
               </div>
               
               <div className="mt-8 md:mt-10 p-5 md:p-6 bg-orange-50 rounded-xl border border-orange-100">
                  <p className="text-brand-accent font-black text-[9px] md:text-xs uppercase tracking-widest mb-2">Eksperta ieteikums:</p>
                  <p className="text-brand-deep/70 text-xs md:text-sm font-bold leading-relaxed">
                    Neaizmirstiet paņemt līdzi ērtus apavus un saules aizsargkrēmu, jo saule šeit ir aktīva.
                  </p>
               </div>
            </div>

            <div className="bg-brand-deep p-10 md:p-12 rounded-lg text-white shadow-2xl relative overflow-hidden group text-left">
               <div className="absolute top-0 right-0 w-40 h-40 bg-brand-accent/20 rounded-full blur-[70px]"></div>
               <h4 className="text-xl md:text-2xl font-black mb-5 md:mb-6 tracking-tight relative z-10 leading-tight">Vajadzīga palīdzība?</h4>
               <p className="text-white/60 font-bold text-sm md:text-base mb-8 md:mb-10 relative z-10 leading-relaxed">Mūsu ceļojumu eksperti palīdzēs atrast piemērotāko viesnīcu tieši Jums.</p>
               <button 
                 onClick={() => navigate('/kontakti')}
                 className="w-full bg-brand-accent text-white py-4 md:py-5 rounded-lg font-black text-[10px] md:text-xs uppercase tracking-[0.2em] shadow-xl active:scale-95 transition-all relative z-10"
               >
                 Sazināties ar mums
               </button>
            </div>
          </aside>
        </div>

        {/* Dynamic Trip Section */}
        <section className="mt-20 md:mt-32 text-left">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-16 gap-6 md:gap-8">
            <div className="space-y-3 md:space-y-4">
               <div className="inline-block bg-brand-surface text-brand-deep/30 px-5 py-2 rounded-full text-[10px] md:text-xs font-black uppercase tracking-[0.2em] border border-gray-100">Aktuālie piedāvājumi</div>
               <h2 className="text-2xl md:text-4xl font-black text-brand-deep tracking-tighter">
                 Ceļojumi uz <span className="text-brand-accent">{country.name}</span>
               </h2>
            </div>
            <button 
              onClick={() => navigate(`/celojumi?destination=${encodeURIComponent(country.name)}`)}
              className="w-full md:w-auto group border-2 border-brand-deep rounded-lg px-8 py-3.5 md:px-10 md:py-4 flex items-center justify-center gap-4 hover:bg-brand-deep hover:text-white transition-all shadow-xl"
            >
              <span className="text-[10px] md:text-xs font-black uppercase tracking-widest">Skatīt visus</span>
              <i className="fas fa-arrow-right text-brand-accent group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>

          {countryPackages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {countryPackages.map(pkg => (
                <PackageCard key={pkg.id} pkg={pkg} />
              ))}
            </div>
          ) : (
            <div className="bg-white p-12 md:p-24 rounded-lg text-center border-2 border-dashed border-gray-100">
               <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8 text-brand-deep/10 text-3xl md:text-4xl">
                 <i className="fas fa-plane-slash"></i>
               </div>
               <h3 className="text-2xl md:text-3xl font-black text-brand-deep mb-4">Šobrīd piedāvājumu nav</h3>
               <p className="text-brand-deep/40 font-bold mb-8 md:mb-10 text-sm md:text-lg">Sazinieties ar mums individuālam maršrutam!</p>
               <button onClick={() => navigate('/kontakti')} className="bg-brand-deep text-white px-8 md:px-10 py-3 md:py-4 rounded-lg font-black text-[10px] md:text-xs uppercase tracking-widest active:scale-95">Pieprasīt cenu</button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
